// Fase K4 — Offentlig påmeldingsside for åpne turer (/p/:token). Ingen innlogging,
// ingen betaling. Premium, brandet layout: cover-bilde med gradient, to-kolonners
// desktop (venstre = innhold, høyre = «sticky» påmeldingskort), operatørlogo og
// brandfarge som aksent (faller tilbake til emerald + firmanavn hvis ikke satt).
// Data-minimering: kun antall ledige seter vises — aldri navn på andre deltakere.
// Anti-spam: skjult honeypot-felt + signert nonce (server håndhever min-tid).
//
// PREVIEW: komponenten kan mates med `previewData` + `preview` fra
// OpenTripsManager for å vise NØYAKTIG samme side (utkast eller publisert) uten
// token — påmelding er da deaktivert («Forhåndsvisning»), ingenting skrives.
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Loader2, Users, CalendarDays, Clock, MapPin, Ticket, CheckCircle2,
  AlertTriangle, ShieldCheck, Ban, ArrowRight, Facebook, Mail, Link as LinkIcon, Check,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { usePublicLanguage } from '@/components/public/usePublicLanguage';
import PublicLanguageToggle from '@/components/public/PublicLanguageToggle';

const API = process.env.REACT_APP_BACKEND_URL;
const EMERALD = '#059669';

const fmtDate = (iso?: string | null, lang = 'no') => {
  if (!iso) return null;
  try {
    return new Date(iso.slice(0, 10)).toLocaleDateString(lang === 'en' ? 'en-GB' : 'nb-NO', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  } catch { return iso; }
};

const hexToRgba = (hex: string, a: number) => {
  const m = (hex || '').replace('#', '');
  if (m.length !== 6) return `rgba(5,150,105,${a})`;
  const r = parseInt(m.slice(0, 2), 16), g = parseInt(m.slice(2, 4), 16), b = parseInt(m.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${a})`;
};

// Relative luminance (WCAG) → pick readable text/ink for the chosen brand colour.
const relLum = (hex: string) => {
  const m = (hex || '').replace('#', '');
  if (m.length !== 6) return 0.3;
  const lin = [0, 2, 4].map((i) => {
    const c = parseInt(m.slice(i, i + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * lin[0] + 0.7152 * lin[1] + 0.0722 * lin[2];
};
const darkenHex = (hex: string, f: number) => {
  const m = (hex || '').replace('#', '');
  if (m.length !== 6) return hex;
  const p = [0, 2, 4].map((i) => Math.round(parseInt(m.slice(i, i + 2), 16) * (1 - f)));
  return '#' + p.map((x) => Math.max(0, Math.min(255, x)).toString(16).padStart(2, '0')).join('');
};
// Text on a SOLID brand fill (white on dark brand, dark ink on light brand).
const onColor = (hex: string) => (relLum(hex) > 0.45 ? '#0f172a' : '#ffffff');
// Brand used as TEXT on white/soft backgrounds — darken very light brands for contrast.
const inkFor = (hex: string) => (relLum(hex) > 0.55 ? darkenHex(hex, 0.5) : hex);

const STATE_MSG: Record<string, { title: [string, string]; body: [string, string]; icon: any }> = {
  unpublished: { title: ['publicOpenTripPage.state.unpublishedTitle', 'Turen er ikke tilgjengelig'], body: ['publicOpenTripPage.state.unpublishedBody', 'Denne påmeldingen er ikke publisert lenger.'], icon: Ban },
  expired: { title: ['publicOpenTripPage.state.expiredTitle', 'Påmeldingen er stengt'], body: ['publicOpenTripPage.state.expiredBody', 'Lenken har utløpt.'], icon: Clock },
  completed: { title: ['publicOpenTripPage.state.completedTitle', 'Turen er gjennomført'], body: ['publicOpenTripPage.state.completedBody', 'Denne turen er allerede kjørt.'], icon: CalendarDays },
  closed: { title: ['publicOpenTripPage.state.closedTitle', 'Påmeldingsfristen er passert'], body: ['publicOpenTripPage.state.closedBody', 'Det er dessverre ikke mulig å melde seg på lenger.'], icon: Clock },
};

type Props = { previewData?: any; preview?: boolean };

export default function PublicOpenTripPage({ previewData, preview = false }: Props = {}) {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<any>(previewData || null);
  const { lang, setPublicLanguage } = usePublicLanguage(null);
  const toggle = <PublicLanguageToggle lang={lang} onChange={setPublicLanguage} />;
  const [loading, setLoading] = useState(!previewData);
  const [err, setErr] = useState(false);
  const [form, setForm] = useState<any>({
    name: '', seats: 1, phone: '', email: '', message: '', consent: false, website: '',
    party_type: 'person', invoice_name: '', invoice_org: '', invoice_email: '', invoice_phone: '', invoice_address: '',
    participants: [] as any[],
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [formErr, setFormErr] = useState('');
  const [copied, setCopied] = useState(false);

  const load = async () => {
    try {
      const res = await fetch(`${API}/api/public/open-trip/${token}`);
      if (!res.ok) throw new Error('bad');
      setData(await res.json());
    } catch { setErr(true); } finally { setLoading(false); }
  };
  useEffect(() => {
    if (previewData) { setData(previewData); setLoading(false); return; }
    load(); /* eslint-disable-next-line */
  }, [token, previewData]);

  const company = data?.company || {};
  const trip = data?.trip || {};
  const brand = company.primary_color || EMERALD;
  const brandOn = onColor(brand);
  const ink = inkFor(brand);
  const fmtKr = (n: any) => (Number(n) || 0).toLocaleString(lang === 'en' ? 'en-GB' : 'nb-NO');
  const priceTotal = Number(trip.price_total ?? trip.price_per_seat ?? 0);
  const pricingMode = trip.pricing_mode || 'per_person';
  const perLabel = pricingMode === 'per_group' ? t('publicOpenTripPage.perGruppe', 'per gruppe') : t('publicOpenTripPage.perPerson', 'per person');
  const priceComponents = trip.price_components || [];
  const includedLabels = priceComponents.map((c: any) => c.label).join(', ');
  const hasPrice = priceTotal > 0 || priceComponents.length > 0;
  const isFull = data?.state === 'full';
  const canSignup = data?.state === 'open' || data?.state === 'full';
  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const copyLink = () => {
    try { navigator.clipboard.writeText(shareUrl); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch { /* noop */ }
  };

  const addParticipant = () => setForm((f: any) => ({ ...f, participants: [...f.participants, { name: '', phone: '', email: '' }] }));
  const updateParticipant = (i: number, k: string, v: string) =>
    setForm((f: any) => ({ ...f, participants: f.participants.map((p: any, idx: number) => idx === i ? { ...p, [k]: v } : p) }));
  const removeParticipant = (i: number) =>
    setForm((f: any) => ({ ...f, participants: f.participants.filter((_: any, idx: number) => idx !== i) }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (preview) return;
    setFormErr('');
    if (!form.name.trim()) { setFormErr(t('publicOpenTripPage.fyllInnNavn', 'Fyll inn navn.')); return; }
    if (!form.email.includes('@')) { setFormErr(t('publicOpenTripPage.fyllInnGyldigEpost', 'Fyll inn en gyldig e-postadresse.')); return; }
    if (!form.consent) { setFormErr(t('publicOpenTripPage.duMaSamtykke', 'Du må samtykke for å melde deg på.')); return; }
    setSubmitting(true);
    try {
      const invoice_to = form.party_type === 'person' ? null : {
        name: form.invoice_name, org_number: form.invoice_org, email: form.invoice_email,
        phone: form.invoice_phone, address: form.invoice_address,
      };
      const participants = (form.participants || [])
        .filter((p: any) => (p.name || '').trim())
        .map((p: any) => ({ name: p.name, phone: p.phone || '', email: p.email || '' }));
      const res = await fetch(`${API}/api/public/open-trip/${token}/signup`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name, seats: Number(form.seats) || 1, phone: form.phone,
          email: form.email, message: form.message, consent: form.consent,
          website: form.website, nonce: data?.nonce || '',
          party_type: form.party_type, invoice_to, participants,
        }),
      });
      const j = await res.json();
      if (!res.ok) { setFormErr(j?.detail || t('publicOpenTripPage.pameldingenKunneIkkeSendes', 'Påmeldingen kunne ikke sendes.')); return; }
      setResult(j);
    } catch { setFormErr(t('public.somethingWentWrong', 'Noe gikk galt. Prøv igjen.')); } finally { setSubmitting(false); }
  };

  if (loading) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  }
  if (err || !data) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6 relative" data-testid="open-trip-error">
        <div className="absolute right-4 top-4">{toggle}</div>
        <div className="text-center max-w-sm">
          <AlertTriangle className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <h1 className="text-lg font-semibold text-slate-700">{t('publicOpenTripPage.lenkenErIkkeGyldig', 'Lenken er ikke gyldig')}</h1>
          <p className="text-sm text-slate-500 mt-1">{t('publicOpenTripPage.sjekkAtDuHarApnet', 'Sjekk at du har åpnet riktig lenke.')}</p>
        </div>
      </div>
    );
  }

  const PreviewBanner = () => (preview ? (
    <div className="bg-amber-500 text-white text-center text-xs font-semibold py-1.5 tracking-wide" data-testid="open-trip-preview-banner">
      {t('publicOpenTripPage.forhandsvisningPameldingErDeaktivert', 'Forhåndsvisning: påmelding er deaktivert')}
    </div>
  ) : null);

  const SimpleHeader = () => (
    <div className="flex items-center gap-3 mb-6" data-testid="open-trip-brand">
      <div className="ml-auto order-last">{toggle}</div>
      {company.logo ? (
        <img src={company.logo} alt={company.name} className="h-9 w-auto max-w-[140px] object-contain" />
      ) : (
        <div className="h-9 w-9 rounded-lg text-white flex items-center justify-center font-bold" style={{ backgroundColor: brand, color: brandOn }}>{(company.name || 'T')[0]}</div>
      )}
      <span className="text-sm font-semibold text-slate-700">{company.name}</span>
    </div>
  );

  if (data.state === 'cancelled') {
    return (
      <div className="min-h-screen bg-slate-50 px-4 py-10">
        <PreviewBanner />
        <div className="max-w-lg mx-auto"><SimpleHeader />
          <div className="bg-white rounded-2xl border border-red-200 p-8 text-center" data-testid="open-trip-state-cancelled">
            <div className="w-14 h-14 rounded-full mx-auto flex items-center justify-center bg-red-50">
              <Ban className="w-8 h-8 text-red-500" />
            </div>
            <h1 className="text-xl font-bold text-slate-800 mt-4">{t('publicOpenTripPage.turenErDessverreAvlyst', 'Turen er dessverre avlyst')}</h1>
            {trip.title && <p className="text-sm font-medium text-slate-600 mt-2">{trip.title}{trip.trip_date ? ` · ${fmtDate(trip.trip_date, lang)}` : ''}</p>}
            {data.cancel_reason && (
              <div className="mt-4 text-left bg-red-50 rounded-lg p-4 text-sm text-red-700" data-testid="open-trip-cancel-reason-public">
                <p className="font-semibold mb-1">{t('publicOpenTripPage.arsak', 'Årsak')}</p>
                <p className="whitespace-pre-line">{data.cancel_reason}</p>
              </div>
            )}
            <p className="text-xs text-slate-500 mt-4">{t('publicOpenTripPage.taKontaktMedVedSporsmal', 'Ta kontakt med {{name}} hvis du har spørsmål.', { name: company.name || t('publicOpenTripPage.operatoren', 'operatøren') })}</p>
          </div>
        </div>
      </div>
    );
  }

  const stateInfo = STATE_MSG[data.state];
  if (stateInfo && !canSignup) {
    const Icon = stateInfo.icon;
    return (
      <div className="min-h-screen bg-slate-50 px-4 py-10">
        <PreviewBanner />
        <div className="max-w-lg mx-auto"><SimpleHeader />
          <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center" data-testid={`open-trip-state-${data.state}`}>
            <Icon className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <h1 className="text-xl font-bold text-slate-800">{t(...stateInfo.title)}</h1>
            <p className="text-sm text-slate-500 mt-2">{t(...stateInfo.body)}</p>
          </div>
        </div>
      </div>
    );
  }

  // Confirmation view
  if (result) {
    const confirmed = result.status === 'confirmed';
    return (
      <div className="min-h-screen bg-slate-50 px-4 py-10">
        <div className="max-w-lg mx-auto"><SimpleHeader />
          <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center" data-testid="open-trip-confirmation">
            <div className="w-14 h-14 rounded-full mx-auto flex items-center justify-center"
              style={{ backgroundColor: confirmed ? hexToRgba(brand, 0.14) : 'rgba(245,158,11,0.15)' }}>
              {confirmed ? <CheckCircle2 className="w-8 h-8" style={{ color: ink }} /> : <Users className="w-8 h-8 text-amber-600" />}
            </div>
            <h1 className="text-xl font-bold text-slate-800 mt-4" data-testid="open-trip-confirmation-status">
              {confirmed ? t('publicOpenTripPage.pameldingBekreftet', 'Påmelding bekreftet!') : t('publicOpenTripPage.duErPaVenteliste', 'Du er på venteliste')}
            </h1>
            <p className="text-sm text-slate-600 mt-2">{result.message}</p>
            <div className="mt-4 text-left bg-slate-50 rounded-lg p-4 text-sm text-slate-600">
              <p><strong>{trip.title}</strong></p>
              {trip.trip_date && <p>{fmtDate(trip.trip_date, lang)}{trip.departure_time ? ` · ${t('common.atTime', 'kl.')} ${trip.departure_time}` : ''}</p>}
            </div>
            {hasPrice && (
              <p className="text-xs text-slate-500 mt-3">{t('publicOpenTripPage.prisLabel', 'Pris')} {fmtKr(priceTotal)} {trip.currency || 'NOK'} {perLabel}. <strong>{t('publicOpenTripPage.faktureresEtterTurenIngenBetaling', 'Faktureres etter turen, ingen betaling her.')}</strong></p>
            )}
            {result.cancel_url && (
              <a href={result.cancel_url} data-testid="open-trip-cancel-link" className="inline-block mt-5 text-sm text-slate-500 underline hover:text-slate-700">
                {t('publicOpenTripPage.trengerDuAMeldeDegAv', 'Trenger du å melde deg av? Klikk her')}
              </a>
            )}
          </div>
        </div>
      </div>
    );
  }

  const maxSeats = Math.min(trip.max_seats_per_signup || 8, isFull ? (trip.max_seats_per_signup || 8) : Math.max(1, trip.seats_left || 1));
  const inputCls = 'mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[var(--brand)] focus:border-[var(--brand)]';

  const Detail = ({ icon: Icon, label, children }: any) => (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: hexToRgba(brand, 0.1) }}>
        <Icon className="w-4 h-4" style={{ color: ink }} />
      </div>
      <div className="min-w-0">
        <p className="text-[11px] uppercase tracking-wide text-slate-400 font-medium">{label}</p>
        <p className="text-sm text-slate-800 font-medium">{children}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50" style={{ ['--brand' as any]: brand }} data-testid="open-trip-page">
      <PreviewBanner />

      {/* Brandet cover */}
      <header className="relative" data-testid="open-trip-hero">
        {trip.image_url ? (
          <img src={trip.image_url} alt={trip.title} className="w-full h-64 sm:h-80 lg:h-96 object-cover" data-testid="open-trip-image" />
        ) : (
          <div className="w-full h-52 sm:h-64" style={{ background: `linear-gradient(135deg, ${brand}, ${hexToRgba(brand, 0.72)})` }} />
        )}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.74), rgba(0,0,0,0.10))' }} />

        <div className="absolute top-4 right-4 sm:right-6"><PublicLanguageToggle lang={lang} onChange={setPublicLanguage} tone="dark" /></div>
        <div className="absolute top-4 left-4 sm:left-6 flex items-center gap-2.5" data-testid="open-trip-brand">
          {company.logo ? (
            <img src={company.logo} alt={company.name} className="h-9 w-auto max-w-[150px] object-contain drop-shadow" />
          ) : (
            <div className="h-9 w-9 rounded-lg text-white flex items-center justify-center font-bold shadow" style={{ backgroundColor: brand, color: brandOn }}>{(company.name || 'T')[0]}</div>
          )}
          {company.name && <span className="text-sm font-semibold text-white/95 drop-shadow">{company.name}</span>}
        </div>

        <div className="absolute left-0 right-0 bottom-0">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-10 sm:pb-12">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white drop-shadow-lg leading-[1.08] max-w-3xl" data-testid="open-trip-title">{trip.title}</h1>
            <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-white/95 text-sm font-medium">
              {trip.trip_date && <span className="flex items-center gap-1.5"><CalendarDays className="w-4 h-4" />{fmtDate(trip.trip_date, lang)}{trip.departure_time ? ` · ${t('common.atTime', 'kl.')} ${trip.departure_time}` : ''}</span>}
              {(trip.from_location || trip.to_location) && (
                <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4" />{trip.from_location}{trip.to_location ? ` → ${trip.to_location}` : ''}</span>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Innhold */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-16 -mt-4 relative z-10">
        <div className="grid lg:grid-cols-5 gap-6 lg:gap-8">
          {/* Venstre: detaljer + beskrivelse + hva du kan forvente */}
          <div className="lg:col-span-3 space-y-5">
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 sm:p-6">
              <div className="grid sm:grid-cols-2 gap-5">
                {trip.trip_date && <Detail icon={CalendarDays} label={t('publicOpenTripPage.dato', 'Dato')}>{fmtDate(trip.trip_date, lang)}{trip.departure_time ? <span className="block text-slate-500 font-normal">{t('publicOpenTripPage.avreiseKl', { defaultValue: 'Avreise kl. {{v1}}', v1: trip.departure_time })}</span> : null}</Detail>}
                {(trip.from_location || trip.to_location) && (
                  <Detail icon={MapPin} label={t('publicOpenTripPage.reise', 'Reise')}>{trip.from_location}{trip.to_location ? <span className="inline-flex items-center gap-1"> <ArrowRight className="w-3.5 h-3.5 inline text-slate-400" /> {trip.to_location}</span> : ''}</Detail>
                )}
                <div data-testid="open-trip-signup-type">
                  <Detail icon={Ticket} label={t('publicOpenTripPage.pamelding', 'Påmelding')}>
                    {trip.signup_type === 'binding' ? t('publicOpenTripPage.bindendeFaktureresEtterTuren', 'Bindende, faktureres etter turen') : t('publicOpenTripPage.uforpliktendeInteressemelding', 'Uforpliktende interessemelding')}
                  </Detail>
                </div>
                <Detail icon={Users} label={t('publicOpenTripPage.plasser', 'Plasser')}>{isFull ? t('publicOpenTripPage.fulltVenteliste', 'Fullt (venteliste)') : t('publicOpenTripPage.nAvMLedig', '{{left}} av {{cap}} ledig', { left: trip.seats_left, cap: trip.capacity })}</Detail>
              </div>
            </div>

            {trip.description && (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 sm:p-6">
                <h2 className="text-base font-bold text-slate-800 mb-2">{t('publicOpenTripPage.omTuren', 'Om turen')}</h2>
                <p className="text-sm text-slate-600 whitespace-pre-line leading-relaxed" data-testid="open-trip-description">{trip.description}</p>
              </div>
            )}

            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 sm:p-6">
              <h2 className="text-base font-bold text-slate-800 mb-3">{t('publicOpenTripPage.slikFungererDet', 'Slik fungerer det')}</h2>
              <ul className="space-y-3 text-sm text-slate-600">
                {[
                  { icon: Ticket, t: t('publicOpenTripPage.how.step1Title', 'Meld deg på her'), d: t('publicOpenTripPage.how.step1Body', 'Fyll ut skjemaet. Det tar under ett minutt.') },
                  { icon: CheckCircle2, t: t('publicOpenTripPage.how.step2Title', 'Vi bekrefter plassen din'), d: t('publicOpenTripPage.how.step2Body', 'Er turen full, settes du på venteliste og vi kontakter deg.') },
                  { icon: ShieldCheck, t: t('publicOpenTripPage.how.step3Title', 'Ingen betaling på nett'), d: trip.signup_type === 'binding' ? t('publicOpenTripPage.bindendePameldingFaktureresEtterTuren', 'Bindende påmelding. Faktureres etter turen.') : t('publicOpenTripPage.uforpliktendeDuBinderDegIkke', 'Uforpliktende. Du binder deg ikke til noe.') },
                ].map((s, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="mt-0.5 w-7 h-7 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: hexToRgba(brand, 0.1) }}>
                      <s.icon className="w-3.5 h-3.5" style={{ color: ink }} />
                    </div>
                    <span><strong className="text-slate-800">{s.t}.</strong> {s.d}</span>
                  </li>
                ))}
              </ul>
            </div>

            {!preview && (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 sm:p-6" data-testid="open-trip-share">
                <h2 className="text-base font-bold text-slate-800 mb-3">{t('publicOpenTripPage.delTuren', 'Del turen')}</h2>
                <div className="flex flex-wrap gap-2">
                  <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer"
                    data-testid="open-trip-share-facebook"
                    className="inline-flex items-center gap-2 text-sm rounded-lg border border-slate-200 px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors">
                    <Facebook className="w-4 h-4" style={{ color: ink }} /> {t('publicOpenTripPage.facebook', 'Facebook')}
                  </a>
                  <a href={`mailto:?subject=${encodeURIComponent(trip.title || t('publicOpenTripPage.bliMedPaTur', 'Bli med på tur'))}&body=${encodeURIComponent(t('publicOpenTripPage.shareMailBody', 'Bli med på «{{title}}». Meld deg på her: {{url}}', { title: trip.title, url: shareUrl }))}`}
                    data-testid="open-trip-share-email"
                    className="inline-flex items-center gap-2 text-sm rounded-lg border border-slate-200 px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors">
                    <Mail className="w-4 h-4" style={{ color: ink }} /> {t('common.email', 'E-post')}
                  </a>
                  <button type="button" onClick={copyLink} data-testid="open-trip-share-copy"
                    className="inline-flex items-center gap-2 text-sm rounded-lg border border-slate-200 px-3 py-2 text-slate-700 hover:bg-slate-50 transition-colors">
                    {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <LinkIcon className="w-4 h-4" style={{ color: ink }} />}
                    {copied ? t('public.copied', 'Kopiert!') : t('public.copyLink', 'Kopier lenke')}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Høyre: sticky påmeldingskort */}
          <div className="lg:col-span-2">
            <div className="lg:sticky lg:top-6">
              <div className="bg-white rounded-2xl border border-slate-200 shadow-lg p-5 sm:p-6" data-testid="open-trip-signup-card">
                {hasPrice && (
                  <div className="rounded-lg px-3 py-3 text-sm mb-3" style={{ backgroundColor: hexToRgba(brand, 0.1), color: ink }} data-testid="open-trip-price">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="font-semibold">{t('publicOpenTripPage.pris', { defaultValue: 'Pris {{v1}}', v1: perLabel })}</span>
                      <strong className="text-base">{fmtKr(priceTotal)} {trip.currency || 'NOK'}</strong>
                    </div>
                    {includedLabels && <div className="text-xs opacity-80 mt-1">{t('publicOpenTripPage.inkluderer', { defaultValue: 'Inkluderer: {{v1}}', v1: includedLabels.toLowerCase() })}</div>}
                    {priceComponents.length > 0 && (
                      <ul className="mt-2 space-y-0.5 text-xs opacity-90" data-testid="open-trip-price-breakdown">
                        {priceComponents.map((c: any, i: number) => (
                          <li key={i} className="flex justify-between gap-2"><span>{c.label}</span><span>{fmtKr(c.amount)} kr</span></li>
                        ))}
                      </ul>
                    )}
                    {trip.price_note && <div className="text-xs opacity-80 mt-2" data-testid="open-trip-price-note">{trip.price_note}</div>}
                    <span className="block text-xs opacity-80 mt-2">{t('publicOpenTripPage.faktureresEtterTurenIngenBetaling2', 'Faktureres etter turen. Ingen betaling her.')}</span>
                  </div>
                )}

                <div className="rounded-lg px-3 py-2 text-sm font-medium flex items-center gap-2 mb-4"
                  style={isFull ? { backgroundColor: '#f1f5f9', color: '#475569' } : { backgroundColor: hexToRgba(brand, 0.1), color: ink }}
                  data-testid="open-trip-seats-left">
                  <Users className="w-4 h-4" />
                  {isFull ? t('publicOpenTripPage.turenErFullMeldDeg', 'Turen er full. Meld deg på venteliste') : t('publicOpenTripPage.nAvMSeterLedig', '{{left}} av {{cap}} seter ledig', { left: trip.seats_left, cap: trip.capacity })}
                </div>

                <form onSubmit={submit} className="space-y-3" data-testid="open-trip-form">
                  {/* honeypot — visually hidden, must stay empty */}
                  <input type="text" name="website" tabIndex={-1} autoComplete="off" value={form.website}
                    onChange={(e) => setForm({ ...form, website: e.target.value })}
                    className="absolute left-[-9999px] w-px h-px opacity-0" aria-hidden="true" data-testid="open-trip-hp" />

                  <div>
                    <label className="text-xs font-medium text-slate-500">{t('publicOpenTripPage.navn', 'Navn *')}</label>
                    <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required
                      data-testid="open-trip-name" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[var(--brand)] focus:border-[var(--brand)]" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-slate-500">{t('publicOpenTripPage.antallSeter', 'Antall seter *')}</label>
                      <input type="number" min={1} max={maxSeats} value={form.seats}
                        onChange={(e) => setForm({ ...form, seats: Math.max(1, Math.min(maxSeats, Number(e.target.value) || 1)) })}
                        data-testid="open-trip-seats" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[var(--brand)] focus:border-[var(--brand)]" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-500">{t('publicOpenTripPage.telefon', 'Telefon')}</label>
                      <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        data-testid="open-trip-phone" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[var(--brand)] focus:border-[var(--brand)]" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-500">{t('publicOpenTripPage.ePost', 'E-post *')}</label>
                    <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required
                      data-testid="open-trip-email" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[var(--brand)] focus:border-[var(--brand)]" />
                  </div>
                  <div data-testid="open-trip-party-type">
                    <label className="text-xs font-medium text-slate-500">{t('publicOpenTripPage.hvemMelderDuPa', 'Hvem melder du på?')}</label>
                    <div className="mt-1 grid grid-cols-3 gap-2">
                      {[['person', t('publicOpenTripPage.privatperson', 'Privatperson')], ['gruppe', t('publicOpenTripPage.gruppe', 'Gruppe')], ['selskap', t('publicOpenTripPage.selskap', 'Selskap')]].map(([val, lbl]) => (
                        <button key={val} type="button" onClick={() => setForm({ ...form, party_type: val })}
                          data-testid={`open-trip-party-${val}`}
                          className={`rounded-lg border px-2 py-2 text-xs font-medium transition-colors ${form.party_type === val ? '' : 'text-slate-600 bg-white border-slate-300 hover:bg-slate-50'}`}
                          style={form.party_type === val ? { backgroundColor: brand, borderColor: brand, color: brandOn } : {}}>
                          {lbl}
                        </button>
                      ))}
                    </div>
                  </div>

                  {form.party_type !== 'person' && (
                    <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 space-y-2" data-testid="open-trip-invoice-to">
                      <p className="text-xs font-semibold text-slate-600">
                        {form.party_type === 'selskap' ? t('publicOpenTripPage.fakturamottakerSelskap', 'Fakturamottaker (selskap)') : t('publicOpenTripPage.fakturamottakerGruppe', 'Fakturamottaker (gruppe)')}
                      </p>
                      <div>
                        <label className="text-xs font-medium text-slate-500">{form.party_type === 'selskap' ? t('publicOpenTripPage.firmanavn', 'Firmanavn') : t('publicOpenTripPage.gruppenavn', 'Gruppenavn')}</label>
                        <input value={form.invoice_name} onChange={(e) => setForm({ ...form, invoice_name: e.target.value })}
                          data-testid="open-trip-invoice-name" className={inputCls} />
                      </div>
                      {form.party_type === 'selskap' && (
                        <div>
                          <label className="text-xs font-medium text-slate-500">{t('publicOpenTripPage.organisasjonsnummer', 'Organisasjonsnummer')}</label>
                          <input value={form.invoice_org} onChange={(e) => setForm({ ...form, invoice_org: e.target.value })}
                            data-testid="open-trip-invoice-org" className={inputCls} />
                        </div>
                      )}
                      <div>
                        <label className="text-xs font-medium text-slate-500">{t('publicOpenTripPage.fakturaEpost', 'Faktura-e-post')}</label>
                        <input type="email" value={form.invoice_email} onChange={(e) => setForm({ ...form, invoice_email: e.target.value })}
                          data-testid="open-trip-invoice-email" className={inputCls} />
                      </div>
                    </div>
                  )}

                  <div data-testid="open-trip-participants">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-medium text-slate-500">{t('publicOpenTripPage.navnPaDeltakereValgfritt', 'Navn på deltakere (valgfritt)')}</label>
                      <button type="button" onClick={addParticipant} data-testid="open-trip-add-participant"
                        className="text-xs font-semibold" style={{ color: ink }}>+ {t('common.add', 'Legg til')}</button>
                    </div>
                    {form.participants.length > 0 && (
                      <div className="mt-2 space-y-2">
                        {form.participants.map((p: any, i: number) => (
                          <div key={i} className="flex gap-2 items-center" data-testid={`open-trip-participant-row-${i}`}>
                            <input placeholder={t('publicOpenTripPage.fulltNavn', 'Fullt navn')} value={p.name} onChange={(e) => updateParticipant(i, 'name', e.target.value)}
                              data-testid={`open-trip-participant-name-${i}`} className={inputCls + ' flex-1'} />
                            <button type="button" onClick={() => removeParticipant(i)} data-testid={`open-trip-participant-remove-${i}`}
                              className="text-slate-400 hover:text-red-500 shrink-0" aria-label={t('publicOpenTripPage.fjernDeltaker', 'Fjern deltaker')}><Ban className="w-4 h-4" /></button>
                          </div>
                        ))}
                      </div>
                    )}
                    <p className="text-[11px] text-slate-400 mt-1">{t('publicOpenTripPage.laStaTomtHvisDu', 'La stå tomt hvis du bare melder på et antall.')}</p>
                  </div>

                  <div>
                    <label className="text-xs font-medium text-slate-500">{t('publicOpenTripPage.meldingValgfritt', 'Melding (valgfritt)')}</label>
                    <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={2}
                      data-testid="open-trip-message" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[var(--brand)] focus:border-[var(--brand)]" />
                  </div>
                  <label className="flex items-start gap-2 text-xs text-slate-600 cursor-pointer">
                    <input type="checkbox" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })}
                      data-testid="open-trip-consent" className="mt-0.5" style={{ accentColor: brand }} />
                    <span>{t('publicOpenTripPage.samtykkeTekst', 'Jeg samtykker til at {{name}} lagrer opplysningene mine og kontakter meg om denne turen. Opplysningene brukes kun til denne turen.', { name: company.name || t('publicOpenTripPage.operatoren', 'operatøren') })}</span>
                  </label>

                  {formErr && <p className="text-sm text-red-600" data-testid="open-trip-form-error">{formErr}</p>}

                  <button type="submit" disabled={submitting || preview} data-testid="open-trip-submit"
                    style={{ backgroundColor: brand, color: brandOn }}
                    className="w-full rounded-lg text-white font-semibold py-2.5 text-sm transition-all hover:brightness-95 disabled:opacity-60 flex items-center justify-center gap-2">
                    {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                    {preview ? t('publicOpenTripPage.forhandsvisning', 'Forhåndsvisning') : (isFull ? t('publicOpenTripPage.meldMegPaVenteliste', 'Meld meg på venteliste') : t('publicOpenTripPage.meldMegPa', 'Meld meg på'))}
                  </button>
                  <p className="text-[11px] text-slate-400 flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> {t('publicOpenTripPage.ingenBetalingHer', 'Ingen betaling her.')} {trip.signup_type === 'binding' ? t('publicOpenTripPage.bindendeFaktureresEtterTurenDot', 'Bindende. Faktureres etter turen.') : t('publicOpenTripPage.uforpliktendeInteressemeldingDot', 'Uforpliktende interessemelding.')}</p>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
